// src/pages/StoresPage.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import MapPicker from '../components/MapPicker';

const CATEGORIES = ['Fashion', 'Food & Beverage', 'Electronics', 'Beauty', 'Sports', 'Books', 'Toys', 'Jewellery', 'Home & Decor', 'Other'];

const StoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const MapIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
    <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
  </svg>
);

// ── Store List ────────────────────────────────────
export function StoresPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenu, setOpenMenu] = useState(null);

  const load = () => {
    api.getStores().then(data => {
      setStores(data.filter(s => s.ownerId === user?.storeOwner?.id));
    }).finally(() => setLoading(false));
  };

  useEffect(load, [user]);

  const deactivate = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"?`)) return;
    await api.deleteStore(id);
    load();
  };

  return (
    <div className="dash-root">
      <Sidebar active="stores" />
      <main className="dash-main">
        <div className="page-content">
          <div className="page-header">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
                My Stores
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 2 }}>
                {stores.length} store{stores.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/stores/new')}>
              <PlusIcon /> New Store
            </button>
          </div>

          <div className="table-card">
            {loading ? (
              <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
                <div className="spinner" />
              </div>
            ) : stores.length === 0 ? (
              <div className="empty-state">
                <StoreIcon />
                <h3>No stores yet</h3>
                <p>Create your first store to start managing your presence.</p>
                <button className="btn btn-primary" onClick={() => navigate('/stores/new')}>
                  <PlusIcon /> New Store
                </button>
              </div>
            ) : (
              <>
                {openMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setOpenMenu(null)} />}
                {stores.map(store => (
                  <div key={store.id} className="store-row" style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/stores/${store.id}/edit`)}>
                    <div className="store-row-thumb">
                      {store.imageUrl
                        ? <img src={store.imageUrl} alt={store.name} />
                        : <StoreIcon />
                      }
                    </div>
                    <div className="store-row-info">
                      <div className="store-row-name">{store.name}</div>
                      <div className="store-row-meta">
                        {store.category}
                        {store.floorNumber != null && ` · Floor ${store.floorNumber}`}
                        {store.unitNumber && ` · Unit ${store.unitNumber}`}
                      </div>
                    </div>

                    {/* Desktop actions */}
                    <div className="store-row-stats">
                      <span className={`badge ${store.isActive ? 'badge-green' : 'badge-gray'}`}>
                        {store.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <button className="btn btn-secondary" style={{ padding: '6px 10px', gap: 6 }}
                        onClick={e => { e.stopPropagation(); navigate(`/stores/${store.id}/edit`); }}>
                        <EditIcon /> Edit
                      </button>
                      <button className="btn btn-ghost" style={{ padding: '6px 10px', color: 'var(--danger)' }}
                        onClick={e => { e.stopPropagation(); deactivate(store.id, store.name); }}>
                        <TrashIcon />
                      </button>
                    </div>

                    {/* Mobile overflow menu */}
                    <div className="store-row-mobile-actions" style={{ position: 'relative', flexShrink: 0 }}>
                      <button className="btn btn-secondary" style={{ padding: '5px 10px', fontSize: '1.1rem', lineHeight: 1 }}
                        onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === store.id ? null : store.id); }}>
                        ⋮
                      </button>
                      {openMenu === store.id && (
                        <div style={{
                          position: 'absolute', right: 0, top: '110%', zIndex: 10,
                          background: 'var(--surface)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                          minWidth: 150, overflow: 'hidden',
                        }}>
                          {[
                            { label: 'Edit Store', icon: <EditIcon />, action: () => navigate(`/stores/${store.id}/edit`) },
                            { label: 'Deactivate', icon: <TrashIcon />, action: () => deactivate(store.id, store.name), danger: true },
                          ].map(({ label, icon, action, danger }) => (
                            <button key={label} onClick={e => { e.stopPropagation(); action(); setOpenMenu(null); }} style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              width: '100%', padding: '10px 14px', border: 'none',
                              background: 'none', cursor: 'pointer', fontSize: '0.875rem',
                              color: danger ? 'var(--danger)' : 'var(--text)', fontFamily: 'inherit', textAlign: 'left',
                            }}>{icon}{label}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Store Form (New / Edit) ───────────────────────
export function StoreFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [malls, setMalls] = useState([]);
  const [form, setForm] = useState({
    mallId: '', name: '', description: '', category: 'Fashion',
    floorNumber: '', unitNumber: '', latitude: '', longitude: '', imageUrl: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    api.getMalls().then(setMalls);
    if (isEdit) {
      api.getStore(id).then(s => setForm({
        mallId: s.mallId, name: s.name, description: s.description ?? '',
        category: s.category, floorNumber: s.floorNumber ?? '',
        unitNumber: s.unitNumber ?? '', latitude: s.latitude,
        longitude: s.longitude, imageUrl: s.imageUrl ?? '',
      }));
    }
  }, [id, isEdit]);

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const useMyLocation = () => {
    if (!navigator.geolocation) return setError('Geolocation is not supported by your browser.');
    navigator.geolocation.getCurrentPosition(
      pos => setForm(f => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude })),
      () => setError('Could not get location. Please allow location access and try again.')
    );
  };

  const submit = async e => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const payload = {
        ...form,
        floorNumber: form.floorNumber ? parseInt(form.floorNumber) : undefined,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        ownerId: user?.storeOwner?.id,
      };
      if (isEdit) await api.updateStore(id, payload);
      else await api.createStore(payload);
      navigate('/stores');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dash-root">
      <Sidebar active="stores" />
      <main className="dash-main">
        <div className="page-content">
          <div className="page-header">
            <div>
              <button
                onClick={() => navigate('/stores')}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.8rem', cursor: 'pointer', marginBottom: 6, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                ← Stores
              </button>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
                {isEdit ? 'Edit Store' : 'New Store'}
              </h1>
            </div>
          </div>

          <div className="form-card">
            <form onSubmit={submit}>
              <div className="form-grid">
                <div className="form-group full">
                  <label>Mall</label>
                  <select name="mallId" value={form.mallId} onChange={handle} required>
                    <option value="">Select a mall…</option>
                    {malls.map(m => <option key={m.id} value={m.id}>{m.name} — {m.city}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Store Name</label>
                  <input name="name" value={form.name} onChange={handle} required placeholder="e.g. Zara" />
                </div>
                <div className="form-group full">
                  <label>Description</label>
                  <textarea name="description" value={form.description} onChange={handle} rows={3} placeholder="Brief description of your store" />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select name="category" value={form.category} onChange={handle}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Floor Number</label>
                  <input name="floorNumber" type="number" value={form.floorNumber} onChange={handle} placeholder="e.g. 2" />
                </div>
                <div className="form-group">
                  <label>Unit Number</label>
                  <input name="unitNumber" value={form.unitNumber} onChange={handle} placeholder="e.g. B-204" />
                </div>
                <div className="form-group full">
                  <label>Store Location</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                    <button type="button" className="btn btn-secondary" onClick={useMyLocation}>
                      <PinIcon /> Use My Location
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowMap(true)}>
                      <MapIcon /> Pick on Map
                    </button>
                  </div>
                  {form.latitude && form.longitude && (
                    <p className="form-hint" style={{ color: 'var(--success)' }}>
                      Location set: {parseFloat(form.latitude).toFixed(5)}, {parseFloat(form.longitude).toFixed(5)}
                    </p>
                  )}
                  <input type="hidden" name="latitude" value={form.latitude} required />
                  <input type="hidden" name="longitude" value={form.longitude} required />
                </div>
                <div className="form-group full">
                  <label>Image URL (optional)</label>
                  <input name="imageUrl" value={form.imageUrl} onChange={handle} placeholder="https://…" />
                </div>
              </div>

              {error && <div className="alert alert-error" style={{ marginTop: 16 }}>{error}</div>}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/stores')}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Store'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {showMap && (
        <MapPicker
          lat={form.latitude ? parseFloat(form.latitude) : null}
          lng={form.longitude ? parseFloat(form.longitude) : null}
          onSelect={(lat, lng) => setForm(f => ({ ...f, latitude: lat, longitude: lng }))}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  );
}
